import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { convertDoubleToStringPTBR } from '../../../../utils/ConverterUtils';
import { formatDate } from '../../../../utils/DateUtils';
import { successAlert, warnAlert } from '../../alert/defaultAlert';
import { ApiError } from '../../Common/ApiError';
import AsteriskRequired from '../../Common/AsteriskRequired';
import ModalPageEvent from '../../Common/Modal/ModalPageEvent';
import PaymentMethod from '../Common/PaymentMethod/PaymentMethod';
import PaymentMethodSelect from '../Common/PaymentMethod/PaymentMethodSelect';
import { offerToOpenCash } from '../CashFlow/Cash/offerToOpenCash';
import './ChargeActionButton.css';

// Mesmas formas que o backend aceita (SubscriptionChargeService#OUTSIDE_PAYMENT_METHODS): as
// que põem dinheiro no caixa de balcão. Exportada porque o pacote da loja aceita as mesmas.
export const OUTSIDE_PAYMENT_METHODS =[PaymentMethod.CASH, PaymentMethod.DEBIT_CARD, PaymentMethod.CREDIT_CARD, PaymentMethod.PIX];

// Mensalidade ainda por receber. Em apuração (PROCESSING) o dinheiro pode estar chegando
// pelo gateway, e o link expirado não tem cobrança no gateway para baixar.
const PAYABLE_STATUSES = ['PENDING', 'OVERDUE', 'FAILED'];

// Provedores que sabem receber a baixa por fora (receiveInCash no Asaas, payedOutsideGalaxPay
// na Celcoin).
const SUPPORTED_PROVIDERS = ['ASAAS', 'CELCOIN'];

/**
 * Registra a mensalidade que o cliente pagou por fora do gateway: em dinheiro ou na
 * maquininha da loja, ou por PIX direto na conta.
 *
 * O backend lança o valor no caixa aberto de quem registra, como numa venda, e avisa o
 * provedor para parar de cobrar. Sem isso a cobrança seguia aberta lá: o cliente continuava
 * recebendo cobrança e, no vencimento, voltava a aparecer como inadimplente aqui.
 *
 * Só aparece quando a baixa é possível; nos demais casos não renderiza nada, para a tela não
 * oferecer um botão que o backend vai recusar.
 *
 * @param {object} charge cobrança; aceita chargeStatus (listagem) ou status (endpoint resumido)
 * @param {number} branchId filial do cliente, onde o caixa é aberto se o operador não tiver um
 * @param {function} onRegistered recebe a cobrança atualizada devolvida pelo backend
 * @param {boolean} compact botão pequeno, para caber em linha de tabela ou cabeçalho de seção
 */
const OutsidePaymentAction = ({ charge, branchId, onRegistered, compact = false }) => {

    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const chargeStatus = charge?.chargeStatus || charge?.status;
    const canRegister = SUPPORTED_PROVIDERS.includes(charge?.paymentProvider)
        && PAYABLE_STATUSES.includes(chargeStatus)
        && !!charge?.providerChargeId;

    if (!canRegister) {
        return null;
    }

    // Abre sempre sem forma escolhida: reaproveitar a da última baixa faria o operador
    // confirmar "dinheiro" para quem pagou no cartão sem perceber.
    const openModal = () => {
        setPaymentMethod(null);
        setShow(true);
    };

    const confirm = () => {
        if (!paymentMethod?.value) {
            warnAlert(t('validation.required.payment_method'));
            return;
        }
        send(paymentMethod.value);
    };

    // Envia a baixa. Sem caixa aberto, oferece abrir e repete; qualquer outra recusa (inclusive
    // a do provedor) mostra o motivo e mantém o modal, porque nada foi registrado.
    const send = (method) => {
        setSubmitting(true);
        axiosInstance.post(`${Api.subscription.charge}/${charge.id}/outside-payment`, { paymentMethod: method })
            .then(({ data }) => {
                setShow(false);
                onRegistered?.(data);
                successAlert(t('outside_payment_success'));
            })
            .catch((error) => {
                const handled = offerToOpenCash({
                    error,
                    branchId,
                    question: t('cash_flow.open_cash_before_consumption'),
                    openingNote: t('cash_flow.automatic_opening_for_subscription'),
                    onBeforeAsk: () => setShow(false),
                    onOpened: () => send(method),
                    onDeclined: () => setShow(true)
                });
                if (!handled) {
                    ApiError(error);
                }
            })
            .finally(() => setSubmitting(false));
    };

    const providerName = charge.paymentProvider === 'CELCOIN' ? 'Celcoin' : 'Asaas';

    return (
        <>
            {/* Mesmo estilo dos botões do link (ChargeActionButton.css), no tom de recebimento. */}
            <button
                type="button"
                className={`btn${compact ? ' btn-sm' : ''} charge-action-btn charge-action-btn--success`}
                onClick={openModal}
            >
                <i className="fa-solid fa-hand-holding-dollar me-1" aria-hidden="true"></i>
                {t('register_outside_payment')}
            </button>

            <ModalPageEvent
                show={show}
                setShow={setShow}
                centered={true}
                title={t('outside_payment_title')}
                btnText={t('confirm')}
                btnOnClick={confirm}
                btnDisabled={submitting}
                child={
                    <Row className="g-3">
                        <Col md={12}>
                            <div className="d-flex justify-content-between flex-wrap gap-2">
                                <span>
                                    {charge.dueDate ? `${t('due_date')}: ${formatDate(charge.dueDate)}` : ''}
                                </span>
                                <strong>
                                    {t('currency_symbol')} {charge.amount != null ? convertDoubleToStringPTBR(charge.amount) : '0,00'}
                                </strong>
                            </div>
                            <small className="text-muted d-block mt-2">
                                {t('outside_payment_hint', { provider: providerName })}
                            </small>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('outside_payment_how')} <AsteriskRequired /></FormLabel>
                                <PaymentMethodSelect
                                    value={paymentMethod}
                                    onChange={setPaymentMethod}
                                    onlyMethods={OUTSIDE_PAYMENT_METHODS}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                }
            />
        </>
    );
};

export default OutsidePaymentAction;
